import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  FormGroup,
  Label,
  Button,
  Input,
  Row,
  Col,
} from "reactstrap";
import RightPanelCard from "components/CommonComponents/RightPanelCard";
import Select from "react-select";
import ReactQuill from "react-quill";
import {
  SINGLE_CORRECT_POSITIVE_OPTIONS,
  SINGLE_CORRECT_NEGATIVE_OPTIONS,
  MULTI_CORRECT_POSITIVE_OPTIONS,
  MULTI_CORRECT_NEGATIVE_OPTIONS,
  ANSWER_TYPES,
} from "components/constant";
import { useLocation } from "react-router-dom";
import { SELECT_CUSTOM_STYLES } from "constants/CommonConstants";

const initialQBQuestion = {
  question: {
    english: "",
    hindi: "",
    error: "",
  },
  type: { value: null, error: "" },
  marks: { value: "", error: "" },
  options: {
    value: [
      { textEnglish: "", textHindi: "", grade: null },
      { textEnglish: "", textHindi: "", grade: null },
    ],
    error: "",
  },
};

const AddQuestion = ({
  isOpen,
  onClose,
  onSave,
  mode,
  selectedIndex,
  initialData,
  negativeGrading,
}) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const assessmentLanguage = searchParams.get("lang");
  const [currentQB, setCurrentQB] = useState(initialQBQuestion);
  const qbPanelMode = mode;

  const normalizedLanguages = assessmentLanguage
    ? assessmentLanguage.split(",").map((l) => l.trim().toUpperCase())
    : ["ENGLISH"];

  const hasEnglish = normalizedLanguages.includes("ENGLISH");
  const hasHindi = normalizedLanguages.includes("HINDI");

  useEffect(() => {
    if (isOpen && initialData) {
      const fixedOptions = initialData.options.value.map((o) => {
        if (hasEnglish && hasHindi) {
          return {
            textEnglish: o.textEnglish || "",
            textHindi: o.textHindi || "",
            grade: o.grade ?? null,
          };
        }

        return {
          text: o.text || o.textEnglish || "",
          grade: o.grade ?? null,
        };
      });

      setCurrentQB({
        ...initialData,
        options: {
          ...initialData.options,
          value: fixedOptions,
        },
      });
    }
  }, [isOpen, initialData]);

  const hasVisibleText = (html = "") => {
    if (!html) return false;

    const el = document.createElement("div");
    el.innerHTML = html;

    return el.textContent.trim().length > 0;
  };

  const getQBValidationError = (name, value) => {
    switch (name) {
      case "question": {
        const hasEn = hasVisibleText(value?.english);
        const hasHi = hasVisibleText(value?.hindi);

        if (!hasEn && !hasHi) {
          return "Question is required";
        }

        return "";
      }

      case "type":
        return !value ? "Please select answer type" : "";

      case "marks":
        if (value === "" || value === null) return "Please enter marks";
        if (Number(value) <= 0) return "Marks must be greater than 0";
        return "";

      case "options": {
        if (currentQB.type.value === "DESC") return "";

        const filledOptions = value.filter((o) => {
          if (hasEnglish && !hasHindi) {
            return hasVisibleText(o.textEnglish);
          }

          if (!hasEnglish && hasHindi) {
            return hasVisibleText(o.textHindi);
          }

          if (hasEnglish && hasHindi) {
            return hasVisibleText(o.textEnglish) || hasVisibleText(o.textHindi);
          }

          return false;
        });

        if (filledOptions.length < 2) return "Please enter at least 2 options";

        if (value.some((o) => o.grade === null))
          return "Please select grade for all options";

        return "";
      }

      default:
        return "";
    }
  };

  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const updateOptionQuill = (index, value, key) => {
    setCurrentQB((prev) => {
      const updated = [...prev.options.value];

      updated[index] = {
        grade: updated[index].grade ?? null,
        ...(hasEnglish && hasHindi
          ? { textEnglish: "", textHindi: "" }
          : hasEnglish
            ? { textEnglish: "" }
            : { textHindi: "" }),
        [key]: value,
      };

      return {
        ...prev,
        options: {
          ...prev.options,
          value: updated,
          error: "",
        },
      };
    });
  };

  const handleAnswerTypeChange = (selected) => {
    setCurrentQB((prev) => {
      let optionsValue = [];

      if (selected.value === "TF") {
        optionsValue = [
          { text: "True", grade: null },
          { text: "False", grade: null },
        ];
      } else if (selected.value === "DESC") {
        optionsValue = [];
      } else {
        optionsValue = [
          { textEnglish: "", textHindi: "", grade: null },
          { textEnglish: "", textHindi: "", grade: null },
        ];
      }

      return {
        ...prev,
        type: { value: selected.value, error: "" },
        options: {
          value: optionsValue,
          error: "",
        },
      };
    });
  };

  const validateQBQuestion = () => {
    const updated = { ...currentQB };

    updated.question.error = getQBValidationError("question", updated.question);

    updated.type.error = getQBValidationError("type", updated.type.value);

    updated.marks.error = getQBValidationError("marks", updated.marks.value);

    if (updated.type.value) {
      updated.options.error = getQBValidationError(
        "options",
        updated.options.value,
      );
    }

    setCurrentQB(updated);

    return Object.values(updated).every((f) => !f.error);
  };

  const getRemainingPositivePercentage = (currentIndex) => {
    return Math.max(
      0,
      1 -
        currentQB.options.value.reduce((sum, opt, idx) => {
          if (
            idx !== currentIndex &&
            typeof opt.grade === "number" &&
            opt.grade > 0
          ) {
            return sum + opt.grade;
          }
          return sum;
        }, 0),
    );
  };

  const handleQBOptionGradeChange = (index, grade) => {
    const updated = [...currentQB.options.value];

    updated[index] = {
      ...updated[index],
      grade,
    };

    setCurrentQB((p) => ({
      ...p,
      options: {
        ...p.options,
        value: updated,
        error: "",
      },
    }));
  };

  const addQBOption = () => {
    if (currentQB.options.value.length >= 5) return;

    setCurrentQB((p) => ({
      ...p,
      options: {
        value: [
          ...p.options.value,
          hasEnglish && hasHindi
            ? { textEnglish: "", textHindi: "", grade: null }
            : hasEnglish
              ? { textEnglish: "", grade: null }
              : { textHindi: "", grade: null },
        ],
        error: "",
      },
    }));
  };

  const removeQBOption = (index) => {
    if (currentQB.options.value.length <= 2) return;

    setCurrentQB((p) => ({
      ...p,
      options: {
        value: p.options.value.filter((_, i) => i !== index),
        error: "",
      },
    }));
  };

  const getFilteredGradeOptions = (index) => {
    const baseOptions = getGradeOptions();

    if (currentQB.type.value !== "MULTI" || negativeGrading) {
      return baseOptions;
    }

    const remaining = getRemainingPositivePercentage(index);

    return baseOptions.filter((opt) => opt.value <= remaining);
  };

  const addQBQuestion = () => {
    if (!validateQBQuestion()) return;

    onSave(
      {
        question: currentQB.question,
        type: currentQB.type.value,
        marks: currentQB.marks.value,
        options: [...currentQB.options.value],
        negativeGrading,
      },
      selectedIndex,
      qbPanelMode,
    );

    setCurrentQB(initialQBQuestion);
    onClose();
  };

  const saveQBQuestion = () => {
    if (!validateQBQuestion()) return;

    onSave(
      {
        question: currentQB.question,
        type: currentQB.type.value,
        marks: currentQB.marks.value,
        options: [...currentQB.options.value],
        negativeGrading,
      },
      selectedIndex,
      qbPanelMode,
    );

    setCurrentQB(initialQBQuestion);
    onClose();
  };

  const getGradeOptions = () => {
    const type = currentQB.type.value;

    // Description has no grading
    if (type === "DESC") return [];

    // Treat TF same as MCQ
    if (type === "MCQ" || type === "TF") {
      return negativeGrading
        ? SINGLE_CORRECT_NEGATIVE_OPTIONS
        : SINGLE_CORRECT_POSITIVE_OPTIONS;
    }

    if (type === "MULTI") {
      return negativeGrading
        ? MULTI_CORRECT_NEGATIVE_OPTIONS
        : MULTI_CORRECT_POSITIVE_OPTIONS;
    }

    return [];
  };

  const gradeOptions = getGradeOptions();

  return (
    <>
      {isOpen && (
        <RightPanelCard
          isOpen={isOpen}
          onClose={onClose}
          title={qbPanelMode === "VIEW" ? "View Question" : "Add Question"}
          fullHeight
          enableScroll={true}
        >
          <Card className="shadow-none border-0">
            <CardBody>
              {/* Question */}
              <div className="d-flex justify-content-end">
                <i
                  className="fa-solid fa-xmark cursor text-muted"
                  onClick={onClose}
                ></i>
              </div>
              <FormGroup>
                <Label>
                  Question <span className="text-danger">*</span>
                </Label>

                <div
                  className={`quill-wrapper ${
                    currentQB.question.error
                      ? "border border-danger rounded"
                      : ""
                  }`}
                >
                  {assessmentLanguage === "English" && (
                    <ReactQuill
                      theme="snow"
                      modules={quillModules}
                      placeholder="Enter question"
                      value={currentQB.question.english}
                      onChange={(value) =>
                        setCurrentQB((p) => ({
                          ...p,
                          question: {
                            ...p.question,
                            english: value,
                            error: "",
                          },
                        }))
                      }
                      onBlur={() =>
                        setCurrentQB((p) => ({
                          ...p,
                          question: {
                            ...p.question,
                            error: getQBValidationError("question", p.question),
                          },
                        }))
                      }
                    />
                  )}

                  {assessmentLanguage === "Hindi" && (
                    <ReactQuill
                      theme="snow"
                      modules={quillModules}
                      placeholder="प्रश्न हिंदी में लिखें"
                      value={currentQB.question.hindi}
                      onChange={(value) =>
                        setCurrentQB((p) => ({
                          ...p,
                          question: {
                            ...p.question,
                            hindi: value,
                            error: "",
                          },
                        }))
                      }
                      onBlur={() =>
                        setCurrentQB((p) => ({
                          ...p,
                          question: {
                            ...p.question,
                            error: getQBValidationError("question", p.question),
                          },
                        }))
                      }
                    />
                  )}

                  {assessmentLanguage === "English, Hindi" && (
                    <>
                      <Label className="text-muted small">
                        Question (English)
                      </Label>
                      <ReactQuill
                        theme="snow"
                        modules={quillModules}
                        placeholder="Enter question in English"
                        value={currentQB.question.english || ""}
                        onChange={(value) =>
                          setCurrentQB((p) => ({
                            ...p,
                            question: {
                              ...p.question,
                              english: value,
                              error: "",
                            },
                          }))
                        }
                      />

                      <Label className="text-muted small mt-3">
                        Question (Hindi)
                      </Label>
                      <ReactQuill
                        theme="snow"
                        modules={quillModules}
                        placeholder="प्रश्न हिंदी में लिखें"
                        value={currentQB.question.hindi || ""}
                        onChange={(value) =>
                          setCurrentQB((p) => ({
                            ...p,
                            question: {
                              ...p.question,
                              hindi: value,
                              error: "",
                            },
                          }))
                        }
                      />
                    </>
                  )}
                </div>

                {currentQB.question.error && (
                  <small className="validation-error d-block mt-1">
                    {currentQB.question.error}
                  </small>
                )}
              </FormGroup>

              <FormGroup>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>
                        Answer Type <span className="text-danger">*</span>
                      </Label>
                      <Select
                        placeholder="Please select answer type"
                        options={ANSWER_TYPES}
                        styles={SELECT_CUSTOM_STYLES}
                        value={
                          currentQB.type.value
                            ? ANSWER_TYPES.find(
                                (opt) => opt.value === currentQB.type.value,
                              )
                            : null
                        }
                        onChange={handleAnswerTypeChange}
                        className={currentQB.type.error ? "is-invalid" : ""}
                      />
                      {currentQB.type.error && (
                        <small className="invalid-feedback d-block">
                          {currentQB.type.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="6">
                    <FormGroup
                      className={`has-label ${
                        currentQB.marks.error ? "has-danger" : ""
                      }`}
                    >
                      <Label>
                        Marks <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="number"
                        placeholder="Enter marks"
                        value={currentQB.marks.value}
                        onChange={(e) =>
                          setCurrentQB((p) => ({
                            ...p,
                            marks: {
                              value: e.target.value,
                              error: "",
                            },
                          }))
                        }
                      />
                      {currentQB.marks.error && (
                        <small className="text-danger">
                          {currentQB.marks.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </FormGroup>

              {(currentQB.type.value === "MCQ" ||
                currentQB.type.value === "MULTI") && (
                <FormGroup>
                  <Label>
                    Options <span className="text-danger">*</span>
                  </Label>

                  {currentQB.options.value.map((opt, index) => {
                    const filteredOptions = getFilteredGradeOptions(index);
                    const showOptionError =
                      currentQB.options.error &&
                      !hasVisibleText(
                        hasEnglish && hasHindi
                          ? opt.textEnglish || opt.textHindi
                          : hasEnglish
                            ? opt.textEnglish
                            : opt.textHindi,
                      );

                    const showGradeError =
                      currentQB.options.error && opt.grade === null;

                    return (
                      <div key={index} className="mb-2">
                        <Row className="align-items-center">
                          <Col md="8">
                            <div
                              className={
                                showOptionError
                                  ? "border border-danger rounded"
                                  : ""
                              }
                            >
                              {assessmentLanguage === "English" && (
                                <>
                                  <Label className="text-muted small">
                                    {`Option ${index + 1}`}
                                  </Label>
                                  <ReactQuill
                                    theme="snow"
                                    modules={quillModules}
                                    placeholder={`Enter Option ${index + 1}`}
                                    value={opt.textEnglish || ""}
                                    onChange={(value) =>
                                      updateOptionQuill(
                                        index,
                                        value,
                                        "textEnglish",
                                      )
                                    }
                                  />
                                </>
                              )}

                              {assessmentLanguage === "Hindi" && (
                                <>
                                  <Label className="text-muted small">
                                    Please enter option in Hindi
                                  </Label>
                                  <ReactQuill
                                    theme="snow"
                                    modules={quillModules}
                                    placeholder={`विकल्प ${index + 1} हिंदी में लिखें`}
                                    value={opt.textHindi || ""}
                                    onChange={(value) =>
                                      updateOptionQuill(
                                        index,
                                        value,
                                        "textHindi",
                                      )
                                    }
                                  />
                                </>
                              )}

                              {assessmentLanguage === "English, Hindi" && (
                                <>
                                  <Label className="text-muted small">
                                    Option {index + 1} (English)
                                  </Label>
                                  <ReactQuill
                                    theme="snow"
                                    modules={quillModules}
                                    placeholder={`Enter Option ${index + 1} in English`}
                                    value={opt.textEnglish || ""}
                                    onChange={(value) =>
                                      updateOptionQuill(
                                        index,
                                        value,
                                        "textEnglish",
                                      )
                                    }
                                  />

                                  <Label className="text-muted small mt-3">
                                    Option {index + 1} (Hindi)
                                  </Label>
                                  <ReactQuill
                                    theme="snow"
                                    modules={quillModules}
                                    placeholder={`विकल्प ${index + 1} हिंदी में लिखें`}
                                    value={opt.textHindi || ""}
                                    onChange={(value) =>
                                      updateOptionQuill(
                                        index,
                                        value,
                                        "textHindi",
                                      )
                                    }
                                  />
                                </>
                              )}
                            </div>
                            <div className="divider" />
                          </Col>
                          <Col md="3" className="d-flex align-items-center">
                            <div
                              className={
                                showGradeError
                                  ? "border border-danger rounded w-100"
                                  : "w-100"
                              }
                            >
                              <Select
                                options={filteredOptions}
                                value={
                                  filteredOptions.find(
                                    (g) => g.value === opt.grade,
                                  ) || null
                                }
                                onChange={(selected) =>
                                  handleQBOptionGradeChange(
                                    index,
                                    selected.value,
                                  )
                                }
                                placeholder="Grade"
                              />
                            </div>
                            <div className="divider" />
                          </Col>

                          <Col
                            md="1"
                            className="d-flex align-items-center justify-content-center"
                          >
                            <Button
                              color="link"
                              className="p-0 table-action-delete"
                              disabled={currentQB.options.value.length <= 2}
                              onClick={() => removeQBOption(index)}
                            >
                              <i className="fas fa-trash" />
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    );
                  })}
                  {currentQB.options.error && (
                    <small className="validation-error d-block mt-1">
                      {currentQB.options.error}
                    </small>
                  )}

                  <Button color="link" onClick={addQBOption}>
                    <i class="fa-solid fa-plus" /> option
                  </Button>
                </FormGroup>
              )}

              {currentQB.type.value === "TF" && (
                <FormGroup>
                  {currentQB.options.value.map((opt, index) => {
                    const showGradeError =
                      currentQB.options.error && opt.grade === null;

                    return (
                      <Row key={index} className="align-items-center mb-2">
                        <Col md="1">
                          <div className="text-muted">{opt.text}</div>
                        </Col>

                        <Col md="4">
                          <div
                            className={
                              showGradeError
                                ? "border border-danger rounded"
                                : ""
                            }
                          >
                            <Select
                              options={gradeOptions}
                              value={
                                gradeOptions.find(
                                  (g) => g.value === opt.grade,
                                ) || null
                              }
                              onChange={(selected) =>
                                handleQBOptionGradeChange(index, selected.value)
                              }
                              placeholder="Grade"
                            />
                          </div>
                        </Col>
                      </Row>
                    );
                  })}

                  {currentQB.options.error && (
                    <small className="validation-error d-block mt-1">
                      {currentQB.options.error}
                    </small>
                  )}
                </FormGroup>
              )}

              <div className="text-right mt-3">
                <Button
                  color="deep-blue"
                  onClick={
                    qbPanelMode === "VIEW" ? saveQBQuestion : addQBQuestion
                  }
                >
                  {qbPanelMode === "VIEW" ? "Save Question" : "Add Question"}
                </Button>
              </div>
            </CardBody>
          </Card>
        </RightPanelCard>
      )}
    </>
  );
};

export default AddQuestion;
